import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

const AGB = () => {
  const { isDark } = useTheme();
  
  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-[#0a0a0a] text-white' : 'bg-white text-gray-900'}`}>
      {/* Header */}
      <div className={`border-b ${isDark ? 'bg-[#0f0f0f] border-white/5' : 'bg-gray-50 border-gray-200'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link to="/" className={`inline-flex items-center gap-2 transition-colors mb-4 ${isDark ? 'text-[#7BA7C2] hover:text-white' : 'text-[#5A8BA6] hover:text-gray-900'}`}>
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Startseite
          </Link>
          <h1 className="text-3xl font-bold">Allgemeine Geschäftsbedingungen</h1>
          <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Dion Hair Clinic GmbH</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className={`prose max-w-none space-y-10 ${isDark ? 'prose-invert' : ''}`}>
          
          <section>
            <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Einleitung</h2>
            <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
              Die nachfolgenden Allgemeinen Geschäftsbedingungen (AGB) regeln das Vertragsverhältnis zwischen der Dion Hair Clinic GmbH (im Folgenden "Klinik" genannt) und ihren Patienten. Mit der Vereinbarung eines Behandlungstermins oder der Inanspruchnahme von Leistungen der Klinik erkennt der Patient diese AGB an.
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Geltungsbereich</h2>
            <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
              Diese AGB gelten für alle Verträge, die zwischen der Klinik und dem Patienten geschlossen werden, insbesondere für Behandlungsverträge im Bereich der Haartransplantation, Barthaartransplantation, Augenbrauentransplantation und Haarausfalltherapie. Sie gelten auch für alle künftigen Geschäftsbeziehungen, selbst wenn sie nicht nochmals ausdrücklich vereinbart werden.
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Vertragsschluss</h2>
            <p className={`mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Der Behandlungsvertrag kommt durch die Annahme des Behandlungsangebots der Klinik durch den Patienten zustande. Dies kann durch schriftliche Bestätigung, elektronische Kommunikation oder durch konkludentes Handeln erfolgen. Die Klinik behält sich das Recht vor, einen Vertragsschluss ohne Angabe von Gründen abzulehnen.
            </p>
            
            <h3 className={`text-lg font-medium mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Beratungsgespräch</h3>
            <p className={`mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Vor jeder Behandlung findet ein ausführliches Beratungsgespräch statt, in dem die individuellen Behandlungsmöglichkeiten, Risiken und zu erwartenden Ergebnisse besprochen werden. Dieses Gespräch ist unverbindlich und kostenfrei.
            </p>

            <h3 className={`text-lg font-medium mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Behandlungsplan</h3>
            <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
              Nach dem Beratungsgespräch erhält der Patient einen individuellen Behandlungsplan, der die Art der Behandlung, den Umfang, die voraussichtliche Dauer und die Kosten detailliert darstellt.
            </p>
          </section>

          {/* Rest of sections follow the same pattern - abbreviated for space */}
          <section>
            <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Widerruf, Terminabsage und Nichterscheinen</h2>
            
            <h3 className={`text-lg font-medium mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Stornierungsbedingungen</h3>
            <div className={`rounded-xl p-6 border space-y-3 ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}>
              <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>• Kostenfreie Stornierung bis zu <strong className={isDark ? 'text-white' : 'text-gray-900'}>7 Tage</strong> vor dem vereinbarten Behandlungstermin</p>
              <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>• Stornierung innerhalb von 7 Tagen: <strong className={isDark ? 'text-white' : 'text-gray-900'}>20 %</strong> Bearbeitungsgebühr</p>
              <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>• Absage innerhalb von 48 Stunden: <strong className={isDark ? 'text-white' : 'text-gray-900'}>50 %</strong> der Behandlungskosten</p>
              <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>• Nichterscheinen ohne Absage: <strong className={isDark ? 'text-white' : 'text-gray-900'}>voller Betrag</strong> fällig</p>
            </div>
          </section>

          <section>
            <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Datenschutz</h2>
            <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
              Der Patient erklärt sich mit der Erhebung, Verarbeitung und Nutzung seiner personenbezogenen Daten und Gesundheitsdaten zum Zwecke der Durchführung des Behandlungsvertrages einverstanden. Weitere Informationen finden Sie in unserer{' '}
              <Link to="/datenschutz" className={isDark ? 'text-[#7BA7C2] hover:underline' : 'text-[#5A8BA6] hover:underline'}>Datenschutzerklärung</Link>.
            </p>
          </section>

        </div>
      </div>
    </div>
  );
};

export default AGB;
